'use client'

import * as React from 'react'
import { formatPrice } from '@/lib/utils'
import { useSearchStore, useUIStore, useUserStore } from '@/lib/store'
import { ListingCard } from '@/components/listings/listing-card'
import { useMapClusters } from './use-map-clusters'
import type { Listing } from '@/lib/types'
import type { MapFeature } from './use-map-clusters'

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN

interface ListingMapProps {
  onLoadMore?: () => void
  isLoadingMore?: boolean
}

function createPriceMarkerEl(listing: Listing, isSaved: boolean): HTMLDivElement {
  const el = document.createElement('div')
  el.className = 'listing-marker'
  el.innerHTML = `
    <div class="marker-price">
      ${isSaved ? '<span style="color: #ef4444; margin-right: 5px; font-size: 21px; line-height: 1;">♥</span>' : ''}${formatPrice(listing.price)}
    </div>
  `
  el.style.cssText = `
    cursor: pointer;
    padding: 8px;
    margin: -8px;
  `

  const priceDiv = el.querySelector('.marker-price') as HTMLElement
  if (priceDiv) {
    priceDiv.style.cssText = `
      background: hsl(var(--card));
      color: hsl(var(--card-foreground));
      border: 2px solid hsl(var(--border));
      padding: 6px 10px;
      border-radius: 9999px;
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    `
  }

  el.addEventListener('mouseenter', () => {
    el.style.zIndex = '10'
    if (priceDiv) {
      priceDiv.style.transform = 'scale(1.1)'
      priceDiv.style.background = 'hsl(var(--primary))'
      priceDiv.style.color = 'hsl(var(--primary-foreground))'
      priceDiv.style.borderColor = 'hsl(var(--primary))'
      priceDiv.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)'
    }
  })

  el.addEventListener('mouseleave', () => {
    el.style.zIndex = '1'
    if (priceDiv) {
      priceDiv.style.transform = 'scale(1)'
      priceDiv.style.background = 'hsl(var(--card))'
      priceDiv.style.color = 'hsl(var(--card-foreground))'
      priceDiv.style.borderColor = 'hsl(var(--border))'
      priceDiv.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'
    }
  })

  return el
}

function createClusterMarkerEl(pointCount: number): HTMLDivElement {
  // Outer element: no transition — Mapbox GL repositions this via transform: translate()
  // during panning. A CSS transition here would cause the marker to lag behind the map.
  const el = document.createElement('div')
  el.className = 'cluster-marker'
  el.style.cssText = `cursor: pointer;`

  // Inner element: carries all visual styling + hover transition
  const inner = document.createElement('div')

  let size: number
  if (pointCount < 10) size = 36
  else if (pointCount < 50) size = 44
  else size = 52

  inner.style.cssText = `
    width: ${size}px;
    height: ${size}px;
    border-radius: 50%;
    background: hsl(var(--primary));
    color: hsl(var(--primary-foreground));
    border: 3px solid hsl(var(--primary-foreground) / 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${size < 40 ? 13 : 14}px;
    font-weight: 700;
    box-shadow: 0 2px 10px rgba(0,0,0,0.25);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  `

  inner.textContent = pointCount >= 1000 ? `${Math.round(pointCount / 100) / 10}k` : String(pointCount)
  el.appendChild(inner)

  el.addEventListener('mouseenter', () => {
    inner.style.transform = 'scale(1.15)'
    inner.style.boxShadow = '0 4px 14px rgba(0,0,0,0.35)'
  })

  el.addEventListener('mouseleave', () => {
    inner.style.transform = 'scale(1)'
    inner.style.boxShadow = '0 2px 10px rgba(0,0,0,0.25)'
  })

  return el
}

export function ListingMap({ onLoadMore, isLoadingMore }: ListingMapProps) {
  const { results, isLoading, hiddenListings, showAllHidden, totalCount } = useSearchStore()
  const { theme } = useUIStore()
  const { savedListingIds } = useUserStore()
  const mapContainerRef = React.useRef<HTMLDivElement>(null)
  const mapRef = React.useRef<any>(null)
  const markersRef = React.useRef<Map<string, any>>(new Map())
  const [mapLoaded, setMapLoaded] = React.useState(false)
  const [mapError, setMapError] = React.useState<string | null>(null)
  const [selectedListing, setSelectedListing] = React.useState<Listing | null>(null)

  const hiddenCount = hiddenListings.size

  const listingsWithLocation = React.useMemo(
    () => results.filter((l) => l.location?.lat && l.location?.lon && !hiddenListings.has(l.id)),
    [results, hiddenListings]
  )

  // Legacy (unused) map: the cluster hook now indexes skinny map points, so
  // adapt the listings it already has rather than change its own data flow.
  const legacyPoints = React.useMemo(
    () =>
      listingsWithLocation.map((l) => ({
        id: l.id,
        lat: l.location!.lat,
        lon: l.location!.lon,
        price: l.price,
        price_currency: l.price_currency,
        district: l.district ?? null,
        area: l.area ?? null,
        rooms: l.rooms ?? null,
      })),
    [listingsWithLocation]
  )
  const byId = React.useMemo(
    () => new Map(listingsWithLocation.map((l) => [l.id, l])),
    [listingsWithLocation]
  )

  const { getClusters, getClusterExpansionZoom } = useMapClusters(legacyPoints)

  // Clear selected listing when component unmounts
  React.useEffect(() => {
    return () => {
      setSelectedListing(null)
    }
  }, [])

  // Determine actual theme (resolve 'system')
  const [actualTheme, setActualTheme] = React.useState<'light' | 'dark'>('light')

  React.useEffect(() => {
    const getActualTheme = () => {
      if (theme === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      }
      return theme as 'light' | 'dark'
    }
    setActualTheme(getActualTheme())

    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = (e: MediaQueryListEvent) => setActualTheme(e.matches ? 'dark' : 'light')
      mediaQuery.addEventListener('change', handler)
      return () => mediaQuery.removeEventListener('change', handler)
    }
  }, [theme])

  const mapStyle = actualTheme === 'dark'
    ? 'mapbox://styles/mapbox/dark-v11'
    : 'mapbox://styles/mapbox/streets-v12'

  // The core marker update: query supercluster for viewport, diff against existing markers
  const updateMarkers = React.useCallback(() => {
    const map = mapRef.current
    if (!map) return

    const bounds = map.getBounds()
    if (!bounds) return
    const bbox: [number, number, number, number] = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ]
    const zoom = map.getZoom()

    const features = getClusters(bbox, zoom)
    const nextKeys = new Set<string>()

    // Build set of keys we need
    features.forEach((f: MapFeature) => {
      const key = f.type === 'cluster' ? `cluster-${f.id}` : `listing-${f.id}`
      nextKeys.add(key)
    })

    // Remove markers no longer in viewport
    const existing = markersRef.current
    existing.forEach((marker, key) => {
      if (!nextKeys.has(key)) {
        marker.remove()
        existing.delete(key)
      }
    })

    // We need mapboxgl for creating markers
    const mapboxgl = require('mapbox-gl')

    // Add new markers
    features.forEach((f: MapFeature) => {
      const key = f.type === 'cluster' ? `cluster-${f.id}` : `listing-${f.id}`

      if (existing.has(key)) return // already rendered

      if (f.type === 'cluster') {
        const el = createClusterMarkerEl(f.pointCount)
        const clusterId = f.id

        el.addEventListener('click', () => {
          const expansionZoom = getClusterExpansionZoom(clusterId)
          map.flyTo({ center: [f.lng, f.lat], zoom: expansionZoom, duration: 300 })
        })

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([f.lng, f.lat])
          .addTo(map)
        existing.set(key, marker)
      } else {
        const isSaved = savedListingIds.includes(f.id)
        const legacyListing = byId.get(f.id)
        if (!legacyListing) return
        const el = createPriceMarkerEl(legacyListing, isSaved)

        el.addEventListener('click', () => {
          setSelectedListing(legacyListing)
        })

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([f.lng, f.lat])
          .addTo(map)
        existing.set(key, marker)
      }
    })
  }, [getClusters, getClusterExpansionZoom, savedListingIds])

  // Initialize map
  React.useEffect(() => {
    if (!MAPBOX_TOKEN) {
      setMapError('MAPBOX_ACCESS_TOKEN not configured')
      return
    }

    if (!mapContainerRef.current || mapRef.current) return

    let isMounted = true

    const initMap = async () => {
      try {
        const mapboxgl = (await import('mapbox-gl')).default
        await import('mapbox-gl/dist/mapbox-gl.css')

        if (!isMounted || !mapContainerRef.current) return

        mapboxgl.accessToken = MAPBOX_TOKEN

        const defaultCenter: [number, number] = [14.4378, 50.0755]

        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: mapStyle,
          center: defaultCenter,
          zoom: 11,
        })

        map.addControl(new mapboxgl.NavigationControl(), 'top-right')

        map.on('load', () => {
          if (isMounted) {
            mapRef.current = map
            setMapLoaded(true)
          }
        })

        map.on('error', (e: any) => {
          console.error('Mapbox error:', e)
          setMapError('Failed to load map')
        })

      } catch (err) {
        console.error('Failed to initialize Mapbox:', err)
        if (isMounted) {
          setMapError('Failed to initialize map')
        }
      }
    }

    initMap()

    return () => {
      isMounted = false
      markersRef.current.clear()
      if (mapRef.current) {
        const map = mapRef.current
        mapRef.current = null
        const deferCleanup = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 1))
        deferCleanup(() => {
          map.remove()
        })
      }
    }
  }, [])

  // Update map style when theme changes
  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded) return
    mapRef.current.setStyle(mapStyle)
  }, [mapStyle, mapLoaded])

  // Listen to moveend to re-render markers on pan/zoom
  React.useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    const onMoveEnd = () => updateMarkers()
    map.on('moveend', onMoveEnd)

    // Also update after style changes (theme switch removes sources/layers but DOM markers survive)
    map.on('styledata', onMoveEnd)

    return () => {
      map.off('moveend', onMoveEnd)
      map.off('styledata', onMoveEnd)
    }
  }, [mapLoaded, updateMarkers])

  // When listings change: fit bounds, then moveend will trigger marker rendering
  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    // Clear all existing markers since the dataset changed
    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current.clear()

    if (listingsWithLocation.length === 0) return

    const mapboxgl = require('mapbox-gl')
    const bounds = new mapboxgl.LngLatBounds()
    listingsWithLocation.forEach((listing) => {
      if (listing.location) {
        bounds.extend([listing.location.lon, listing.location.lat])
      }
    })

    mapRef.current.fitBounds(bounds, {
      padding: 60,
      maxZoom: 15,
      duration: 500,
    })

    // fitBounds fires moveend which triggers updateMarkers
  }, [listingsWithLocation, mapLoaded])

  // Re-render visible markers when savedListingIds change
  // Since we only have ~20-50 markers in viewport, clearing and re-rendering is fast
  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    // Clear and re-render to pick up saved state changes
    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current.clear()
    updateMarkers()
  }, [savedListingIds, mapLoaded, updateMarkers])

  // Show placeholder if no token
  if (!MAPBOX_TOKEN || mapError) {
    return (
      <div className="relative w-full h-[600px] rounded-xl overflow-hidden border border-border">
        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-b from-blue-100 to-blue-200 dark:from-slate-800 dark:to-slate-900">
          <p className="text-lg font-medium text-foreground mb-2">
            Map View
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            {listingsWithLocation.length} listings with location data
          </p>
          <p className="text-xs text-muted-foreground max-w-md text-center px-4">
            {mapError || 'Configure NEXT_PUBLIC_MAPBOX_TOKEN in frontend/.env.local to enable interactive map'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full h-[600px] rounded-xl overflow-hidden border border-border">
      {/* Map container */}
      <div
        ref={mapContainerRef}
        className="w-full h-full"
      />

      {/* Loading overlay - don't unmount the map */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/90 flex items-center justify-center z-20">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-muted-foreground">Loading listings...</p>
          </div>
        </div>
      )}

      {/* Listing count badge */}
      {!isLoading && (
        <div className="absolute top-4 left-4 bg-card rounded-lg px-3 py-1.5 border border-border shadow-sm z-10">
          <p className="text-sm font-medium">
            {listingsWithLocation.length} of {totalCount} listings on map
          </p>
        </div>
      )}

      {/* Load more button for map */}
      {!isLoading && results.length < totalCount && onLoadMore && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium shadow-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoadingMore ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                Loading...
              </>
            ) : (
              <>Load {Math.min(100, totalCount - results.length)} more listings</>
            )}
          </button>
        </div>
      )}

      {/* Hidden listings banner */}
      {!isLoading && hiddenCount > 0 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 max-w-md w-full px-4">
          <div className="flex items-center justify-between px-2 py-2 bg-muted rounded-lg border border-border shadow-sm">
            <span className="text-sm text-muted-foreground">
              {hiddenCount} listing{hiddenCount > 1 ? 's' : ''} hidden
            </span>
            <button
              onClick={showAllHidden}
              className="text-sm font-medium text-primary hover:underline"
            >
              Show all
            </button>
          </div>
        </div>
      )}

      {/* Selected Listing Card */}
      {selectedListing && (
        <div className="absolute left-4 right-4 top-16 bottom-4 pointer-events-none z-20">
          <div className="relative h-full w-full max-w-md pointer-events-auto">
            {/* Close button */}
            <button
              onClick={() => setSelectedListing(null)}
              className="absolute -top-2 -right-2 z-30 p-2 rounded-full bg-background border-2 border-border shadow-lg hover:bg-muted transition-colors"
              title="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Scrollable card container */}
            <div className="h-full overflow-y-auto overflow-x-hidden rounded-xl shadow-2xl bg-background">
              <ListingCard listing={selectedListing} variant="map" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
